import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader } from 'lucide-react';
import { Header } from '../components/Header';
import { categoriesAPI, transactionsAPI, aiAPI, authAPI, householdsAPI } from '../api/client';
import { useCategories } from '../hooks/useCategories';

interface ExpenseFormData {
  amount: string;
  category: string;
  date: string;
  description: string;
  merchant: string;
}

export function AddExpensePage() {
  const navigate = useNavigate();
  const { categories } = useCategories();
  const [formData, setFormData] = useState<ExpenseFormData>({
    amount: '',
    category: '',
    date: new Date().toISOString().split('T')[0], // Today's date
    description: '',
    merchant: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<ExpenseFormData>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [householdId, setHouseholdId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);

  useEffect(() => {
    loadHousehold();
  }, []);

  const loadHousehold = async () => {
    try {
      setLoading(true);
      
      // Get user and households using the proper API client
      const authResponse = await authAPI.getUser();
      
      if (!authResponse.success || !authResponse.data) {
        throw new Error('Authentication required');
      }

      const { households } = authResponse.data;
      
      let currentHouseholdId: string;

      if (!households || households.length === 0) {
        // Create a default household if none exists
        console.log('No household found, creating default household...');
        const createResponse = await householdsAPI.createHousehold({ 
          name: 'Personal Budget',
          currency: 'USD'
        });
        
        if (createResponse.success && createResponse.data) {
          currentHouseholdId = createResponse.data.household.id;
          console.log('Created new household:', currentHouseholdId);
        } else {
          throw new Error('Failed to create household');
        }
      } else {
        // Use the first household
        currentHouseholdId = households[0].id;
        console.log('Using existing household:', currentHouseholdId);
      }

      setHouseholdId(currentHouseholdId);
    } catch (error) {
      console.error('Failed to load household:', error);
      // Show user-friendly error
      setGeneralError(error instanceof Error ? error.message : 'Failed to load household data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name as keyof ExpenseFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }

    // Auto-categorize when description or merchant changes
    if ((name === 'description' || name === 'merchant') && value.length > 3) {
      handleAICategorize(value, name === 'merchant' ? formData.description : value, name === 'merchant');
    }
  };

  const handleAICategorize = async (description: string, merchant: string, isMerchantField: boolean) => {
    try {
      const response = await aiAPI.categorizeTransaction({
        description,
        merchant
      });

      if (response.success && response.data) {
        const suggestedCategory = response.data.suggestedCategory;
        
        // Find matching category in our list
        const matchingCategory = categories.find(cat => 
          cat.name.toLowerCase() === suggestedCategory.toLowerCase()
        );

        if (matchingCategory) {
          setAiSuggestion(suggestedCategory);
          if (isMerchantField) {
            setFormData(prev => ({ ...prev, category: matchingCategory.id }));
          }
        }
      }
    } catch (error) {
      // Silently fail for AI categorization
      console.log('AI categorization failed:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ExpenseFormData> = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }

    if (!formData.category) {
      newErrors.category = 'Please select a category';
    }

    if (!formData.date) {
      newErrors.date = 'Please select a date';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Please enter a description';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    // Check if household is loaded
    if (!householdId) {
      setGeneralError('Unable to load household data. Please refresh the page and try again.');
      return;
    }

    setIsSubmitting(true);
    setGeneralError(null); // Clear any previous errors

    try {
      const expenseData = {
        amount: parseFloat(formData.amount),
        category_id: formData.category,
        description: formData.description.trim(),
        merchant: formData.merchant.trim(),
        transaction_date: formData.date
      };

      console.log('Creating transaction with data:', expenseData);
      const response = await transactionsAPI.createTransaction(householdId, expenseData);
      console.log('Transaction API response:', response);
      
      if (response.success) {
        // Store the new transaction locally for immediate dashboard update
        try {
          const newTransaction = {
            id: `tx-${Date.now()}`,
            amount: parseFloat(formData.amount),
            category: categories.find(cat => cat.id === formData.category)?.name || 'Unknown',
            description: formData.description.trim(),
            merchant: formData.merchant.trim(),
            date: formData.date
          };
          
          console.log('Storing transaction locally:', newTransaction);
          
          // Store in localStorage for dashboard to pick up
          const existingTransactions = JSON.parse(localStorage.getItem('autobudgetai-transactions') || '[]');
          const updatedTransactions = [newTransaction, ...existingTransactions];
          localStorage.setItem('autobudgetai-transactions', JSON.stringify(updatedTransactions));
          
          // Trigger dashboard refresh
          window.dispatchEvent(new CustomEvent('transactionAdded', { detail: newTransaction }));
          
          console.log('Transaction created successfully!');
        } catch (localStorageError) {
          console.error('Failed to update local transaction data:', localStorageError);
          // Continue anyway - the API call was successful
        }
        
        // Show success message briefly then redirect
        alert('Expense added successfully!');
        
        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        throw new Error(response.error || 'Failed to create transaction');
      }
    } catch (error) {
      console.error('Error submitting expense:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit expense. Please try again.';
      setGeneralError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        <Header />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <Header />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Add New Expense</h1>
            <p className="text-gray-600 dark:text-gray-400">Record a new expense to track your spending</p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error Display */}
            {generalError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
                    <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                      <p>{generalError}</p>
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => setGeneralError(null)}
                        className="text-sm bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 rounded-md px-2 py-1 hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Amount */}
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Amount *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={handleInputChange}
                  className={`block w-full pl-7 pr-3 py-3 border rounded-md text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.amount ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="0.00"
                />
              </div>
              {errors.amount && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.amount}</p>}
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category *
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className={`block w-full px-3 py-3 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.category ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {aiSuggestion && (
                <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                  💡 AI suggests: {aiSuggestion}
                </p>
              )}
              {errors.category && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.category}</p>}
            </div>

            {/* Date */}
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date *
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className={`block w-full px-3 py-3 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.date ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.date && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.date}</p>}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description *
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className={`block w-full px-3 py-3 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.description ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="What did you spend on?"
              />
              {errors.description && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description}</p>}
            </div>

            {/* Merchant */}
            <div>
              <label htmlFor="merchant" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Merchant <span className="text-gray-400 dark:text-gray-500 font-normal">(Optional)</span>
              </label>
              <input
                type="text"
                id="merchant"
                name="merchant"
                value={formData.merchant}
                onChange={handleInputChange}
                className={`block w-full px-3 py-3 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.merchant ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Where did you spend this?"
              />
              {errors.merchant && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.merchant}</p>}
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t dark:border-gray-700">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !householdId}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="animate-spin h-4 w-4 mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Expense
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}